
































































































































import { createComponent, computed, ref, onUpdated, watch } from '@vue/composition-api'
import copy from 'copy-to-clipboard'
import { getFactoryStatus, getStatusBorderColor } from '@/lib/map'
import { getFactoryTypeText } from '@/lib/factory'
import useScroll from '@/lib/hooks/useScroll'
import { getFactoryReportRecords } from '@/api'

import { useAppState } from '../lib/appState'
import { FactoryImage, getDisplayStatusText, ReportRecord } from '../types'

export default createComponent({
  name: 'FactoryDetailPage',
  setup () {
    const [appState, { pageTransition, expandFactoryDetail, collapseFactoryDetail, toggleFactoryDetail }] = useAppState()

    const images = computed(() => {
      if (appState.factoryData) {
        return appState.factoryData.images
      } else {
        return []
      }
    })

    const lightboxItems = computed(() => {
      if (appState.factoryData) {
        return appState.factoryData.images.map(image => ({
          title: '',
          description: '',
          src: image.url
        }))
      } else {
        return []
      }
    })
    const lightboxIndex = ref<number|null>(null)
    const setLightboxIndex = (i: number) => {
      lightboxIndex.value = i
    }

    const factoryStatus = computed(() => {
      if (appState.factoryData) {
        return getFactoryStatus(appState.factoryData)
      } else {
        return undefined
      }
    })

    const factoryStatusText = computed(() => {
      if (typeof factoryStatus.value !== 'undefined') {
        const factoryDisplayStatus = getDisplayStatusText(factoryStatus.value)
        const { document_display_status: documentDisplayStatus } = appState.factoryData || {}
        if (documentDisplayStatus) {
          return `${factoryDisplayStatus}：${documentDisplayStatus}`
        }
        return factoryDisplayStatus
      } else {
        return ''
      }
    })

    const statusColor = computed(() => {
      if (typeof factoryStatus.value !== 'undefined') {
        return getStatusBorderColor(factoryStatus.value)
      } else {
        return null
      }
    })

    const factoryId = computed(() => {
      if (appState.factoryData) {
        return appState.factoryData.display_number
      } else {
        return ''
      }
    })

    const factoryType = computed(() => {
      if (appState.factoryData) {
        return getFactoryTypeText(appState.factoryData)
      } else {
        return null
      }
    })

    const factoryName = computed(() => {
      return appState.factoryData?.name || ''
    })

    const factoryAddressAndLandcode = computed(() => {
      if (appState.factoryData) {
        const { townname, sectname, sectcode, landcode } = appState.factoryData
        // 白鷺段（1005）817地號
        return `${townname} ${sectname} (${sectcode}) ${landcode}地號`
      } else {
        return ''
      }
    })

    const source = computed(() => {
      if (appState.factoryData) {
        return appState.factoryData.source?.split('')
          .map(function getName (source) {
            switch (source) {
              case 'U':
                return '本系統'
              case 'G':
                return '農委會'
              case 'E':
                return '經濟部'
            }
          })
          .join('、')
      }

      return ''
    })

    const full = computed(() => appState.factoryDetailsExpanded)

    const longitude = computed(() => appState.factoryData?.lng.toFixed(7))
    const latitude = computed(() => appState.factoryData?.lat.toFixed(7))

    const factoryDetailScrollerRef = ref(null)
    const { scrollTop } = useScroll(factoryDetailScrollerRef)
    const scrollOff = computed(() => scrollTop.value > 29 && full.value)

    const showCopiedMessage = ref(false)
    const copyToClipboard = () => {
      if (!appState.factoryData) {
        return
      }

      const { host, protocol } = window.location
      const url = `${protocol}//${host}/#map=16/${appState.factoryData?.lng}/${appState.factoryData?.lat}&factoryId=${appState.factoryData.id}`

      copy(url)

      showCopiedMessage.value = true
      window.setTimeout(() => {
        showCopiedMessage.value = false
      }, 1000)
    }

    // Workaround https://github.com/vuetifyjs/vuetify/issues/10971
    const slideGroup = ref(null)
    let prevImages: FactoryImage[]
    onUpdated(() => {
      if (JSON.stringify(images.value) === JSON.stringify(prevImages)) {
        return
      } else {
        if (!prevImages) {
          prevImages = images.value.slice()
          return
        } else {
          prevImages = images.value.slice()
        }
      }

      if (!slideGroup.value) {
        return
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const content = (slideGroup.value as any)?.$refs.content
      window.setTimeout(() => {
        content.style = 'translateX(0px)'
      }, 50)
    })

    const startUpdateFactoryCommentFor = (field = 'others') => {
      pageTransition.startUpdateFactoryComment(field)
    }

    const reportRecords = ref<ReportRecord[]>([])
    watch(() => appState.factoryData, function () {
      if (appState.factoryData) {
        (async () => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          reportRecords.value = ((await getFactoryReportRecords(appState.factoryData?.id!)) || []).sort((a, b) => {
            return new Date(b.created_at) >= new Date(a.created_at) ? 1 : -1
          })
        })()
      }
    })
    const pastDescriptions = computed(() => {
      return reportRecords.value.map(record => {
        const date = new Date(record.created_at)
        const dateStr = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`
        return {
          date: dateStr,
          others: record.others
        }
      }).filter(rec => rec.others)
    })
    const lastUpdatedAt = computed(() => {
      if (!appState.factoryData || !appState.factoryData.reported_at) {
        return null
      }

      let date
      if (reportRecords.value.length === 0) {
        date = new Date(appState.factoryData.reported_at)
      } else {
        date = new Date(reportRecords.value[0].created_at)
      }
      const dateStr = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`

      return `最後更新 ${dateStr}`
    })

    return {
      full,
      appState,
      pageTransition,
      images,
      expandFactoryDetail,
      collapseFactoryDetail,
      toggleFactoryDetail,
      factoryStatus,
      factoryStatusText,
      statusColor,
      factoryId,
      factoryType,
      factoryName,
      factoryAddressAndLandcode,
      source,
      pastDescriptions,
      lastUpdatedAt,
      longitude,
      latitude,
      factoryDetailScrollerRef,
      scrollOff,
      showCopiedMessage,
      copyToClipboard,
      slideGroup,
      startUpdateFactoryCommentFor,
      lightboxItems,
      lightboxIndex,
      setLightboxIndex
    }
  }
})
